import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Target, Footprints, Wrench, Lightbulb, Compass, MessageCircle, Star } from "lucide-react";
import { api } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Loader } from "@/components/Loader";
import { ReportEngine } from "@/components/c2g/ReportEngine";

const STATUS = { to_start: "Senza conferma", in_progress: "Iniziato, senza conferma", reduced_pending: "Ridotto, senza conferma", rescheduled: "Riprogrammato, senza conferma", not_completed: "Non svolto" };
const statusLabel = (a) => (a.status === "completed" ? (a.version === "minimal" ? `Completato nella versione ridotta (${(a.minimal || "").replace(/[.\s]+$/, "")})` : "Completato") : STATUS[a.status] || "Esito non indicato");

function Block({ icon: Icon, title, testid, children, i = 0 }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="mt-3 rounded-2xl border border-[#27272A] bg-[#121215] p-5" data-testid={testid}>
      <p className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-[#D4AF37]"><Icon size={14} /> {title}</p>
      <div className="a2g-read mt-2 text-base text-[#D4D4D8]">{children}</div>
    </motion.div>
  );
}

const Missing = ({ children }) => <p className="text-[#8A8E96]">{children}</p>;

export default function Riepilogo() {
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [discovery, setDiscovery] = useState("");
  const [rating, setRating] = useState(null);
  const [eng, setEng] = useState(null);

  useEffect(() => {
    const legacy = () => api.get("/path/summary").then(({ data }) => { setS(data); setDiscovery(data.discovery || ""); setRating(data.rating); }).catch(() => navigate("/oggi"));
    api.get("/c2g/report").then(({ data }) => setEng(data)).catch((e) => (e.response?.status === 409 ? legacy() : navigate("/oggi")));
  }, []); // eslint-disable-line

  if (eng) {
    return (
      <AppShell title="La tua settimana" greeting={`Settimana ${eng.week} · ${eng.profile.dimension} ${eng.profile.level_label}`}>
        <ReportEngine data={eng} onNextWeek={() => nextWeek()} onRepeat={() => repeat()} />
      </AppShell>
    );
  }
  if (!s) return <Loader label="Preparo il tuo riepilogo…" />;
  const ind = s.indicators;
  const weekDone = s.calendar?.week_over || ind.lived >= 7;
  const n = (v, one, many) => `${v} ${v === 1 ? one : many}`;

  async function repeat() {
    if (!window.confirm("Ripetere la settimana? I passi registrati verranno azzerati.")) return;
    try {
      await api.post("/path/restart");
      navigate("/oggi");
    } catch {
      toast.error("Errore, riprova");
    }
  }

  const save = async () => {
    try {
      await api.post("/path/summary", { discovery, rating });
      toast.success("Salvato");
    } catch {
      toast.error("Errore nel salvataggio");
    }
  };

  async function nextWeek() {
    try {
      await api.post("/path/next-week");
      navigate("/oggi");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Non disponibile");
    }
  }

  return (
    <AppShell title="La tua settimana" greeting={`Settimana ${s.week} · ${s.profile.dimension_title} ${s.profile.level_title}`}>
      <div className="px-5 pb-8 pt-6" data-testid="summary-page">
        <Block icon={Target} title="Il tuo obiettivo" testid="summary-goal">
          {s.goal ? <p className="text-[#F4F4F5]">{s.goal}</p> : <Missing>Non hai indicato un obiettivo personale al Giorno 1.</Missing>}
        </Block>

        <Block icon={Footprints} title="I passi che hai registrato" testid="summary-actions" i={1}>
          <p className="tabular-nums">{ind.lived}/7 giorni vissuti · {n(ind.actions_done, "completato", "completati")} · {n(ind.actions_reduced, "completato ridotto", "completati ridotti")} · {n(ind.actions_not, "non svolto", "non svolti")}{ind.actions_pending ? ` · ${ind.actions_pending} senza conferma` : ""}{ind.restarts ? ` · ${n(ind.restarts, "ripartenza", "ripartenze")}` : ""}</p>
          {s.actions.length ? (
            <ul className="mt-3 space-y-2">
              {s.actions.map((a) => (
                <li key={a.day} className="rounded-xl bg-[#0D0D0F] p-3 text-sm">
                  <span className="font-semibold text-[#E5C158]">G{a.day}</span> · {a.action}
                  <span className="ml-1 text-[#8A8E96]">· {statusLabel(a)}</span>
                </li>
              ))}
            </ul>
          ) : <Missing>Non hai registrato passi questa settimana.</Missing>}
        </Block>

        <Block icon={Wrench} title="Adattamenti e ostacoli" testid="summary-obstacles" i={2}>
          <p>{ind.actions_reduced ? `Hai usato la versione ridotta ${ind.actions_reduced} ${ind.actions_reduced === 1 ? "volta" : "volte"}.` : "Non hai usato versioni ridotte."}</p>
          {s.obstacles.length ? <p className="mt-1">Ostacoli che hai indicato: {s.obstacles.join(", ").toLowerCase()}.</p> : <Missing>Non hai indicato ostacoli.</Missing>}
        </Block>

        <Block icon={Lightbulb} title="Cosa ti ha aiutato" testid="summary-helped" i={3}>
          {s.helped.length ? <p>{s.helped.join(", ")}.</p> : <Missing>Non hai indicato strategie utili.</Missing>}
          {s.last_tomorrow && <p className="mt-2 text-sm text-[#B4B4BC]">L'ultima cosa che volevi portare avanti: {s.last_tomorrow}</p>}
        </Block>

        <Block icon={Star} title="La tua scoperta della settimana" testid="summary-discovery" i={4}>
          <textarea value={discovery} onChange={(e) => setDiscovery(e.target.value)} rows={3} maxLength={600} placeholder="Cosa hai capito di te in questi sette giorni?" className="mt-1 w-full resize-none rounded-xl border border-[#27272A] bg-[#09090B] p-3 text-base text-[#F4F4F5] outline-none placeholder:text-[#8A8E96] focus:border-[#D4AF37]/50" data-testid="summary-discovery-input" />
          <p className="mt-3 text-sm text-[#B4B4BC]">Quanto ti è stata utile questa settimana?</p>
          <div className="mt-2 flex gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} onClick={() => setRating(n)} data-testid={`summary-rating-${n}`} className={`h-10 w-10 rounded-full border text-sm font-semibold ${rating === n ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#F4F4F5]" : "border-[#27272A] text-[#8A8E96]"}`}>{n}</button>
            ))}
          </div>
          <button onClick={save} data-testid="summary-save-button" className="btn-outline-gold mt-4 rounded-full px-5 py-2 text-sm">Salva</button>
        </Block>

        <Block icon={Compass} title="Il prossimo passo" testid="summary-next" i={5}>
          <div className="mt-2 flex flex-col gap-2">
            {s.next_week_available && weekDone && <button onClick={nextWeek} data-testid="summary-next-week" className="btn-gold rounded-full px-5 py-3 text-sm font-semibold uppercase tracking-wide">Consolida: Settimana {s.week + 1}</button>}
            <button onClick={repeat} data-testid="summary-repeat-week" className="rounded-full border border-[#3F3F46] px-5 py-3 text-sm font-semibold text-[#E4E4E7]">Ripeti questa settimana</button>
            <button onClick={() => navigate("/scegli")} data-testid="summary-other-path" className="rounded-full border border-[#3F3F46] px-5 py-3 text-sm font-semibold text-[#E4E4E7]">Approfondisci un'altra area</button>
            <button onClick={() => navigate("/coach")} data-testid="summary-coach" className="flex items-center justify-center gap-2 rounded-full border border-[#3F3F46] px-5 py-3 text-sm font-semibold text-[#E4E4E7]"><MessageCircle size={16} /> Scegli il prossimo obiettivo con Coach Mino</button>
          </div>
        </Block>
      </div>
    </AppShell>
  );
}
